import { useState, useEffect } from "react";
import axios from "axios";
import { Cookie, X } from "lucide-react";
import { API } from "../lib/api";

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState({ analytics: false, marketing: false, necessary: true });

  useEffect(() => {
    const savedConsent = localStorage.getItem('ospitai-cookie-consent');
    if (!savedConsent) {
      setShowBanner(true);
    }
  }, []);

  const saveConsent = async (acceptAll = false) => {
    const finalConsent = acceptAll 
      ? { analytics: true, marketing: true, necessary: true }
      : consent;
    
    try {
      await axios.post(`${API}/gdpr/cookie-consent`, finalConsent);
    } catch (e) {
      console.error("Failed to save consent:", e);
    }
    
    localStorage.setItem('ospitai-cookie-consent', JSON.stringify(finalConsent));
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6" data-testid="cookie-banner">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-stone-200 p-6">
        {!showSettings ? (
          <>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Cookie size={20} className="text-teal-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-stone-900">We value your privacy</h3>
                <p className="text-stone-600 text-sm mt-1">
                  We use cookies to enhance your experience. By continuing, you agree to our use of cookies. 
                  You can customize your preferences or accept all cookies.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mt-4 justify-end">
              <button
                onClick={() => setShowSettings(true)}
                className="px-4 py-2 text-sm font-medium text-stone-600 hover:text-stone-800 border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors"
              >
                Customize
              </button>
              <button
                onClick={() => saveConsent(false)}
                className="px-4 py-2 text-sm font-medium text-stone-700 bg-stone-100 rounded-lg hover:bg-stone-200 transition-colors"
              >
                Necessary only
              </button>
              <button
                onClick={() => saveConsent(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-700 rounded-lg hover:bg-teal-800 transition-colors"
              >
                Accept all
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-stone-900">Cookie Settings</h3>
              <button onClick={() => setShowSettings(false)} className="text-stone-400 hover:text-stone-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div>
                  <p className="font-medium text-stone-800">Necessary</p>
                  <p className="text-xs text-stone-500">Required for the website to function</p>
                </div>
                <div className="w-10 h-6 bg-teal-600 rounded-full flex items-center justify-end px-1">
                  <div className="w-4 h-4 bg-white rounded-full" />
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div>
                  <p className="font-medium text-stone-800">Analytics</p>
                  <p className="text-xs text-stone-500">Help us improve our service</p>
                </div>
                <button
                  onClick={() => setConsent({ ...consent, analytics: !consent.analytics })}
                  className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${consent.analytics ? 'bg-teal-600 justify-end' : 'bg-stone-300 justify-start'}`}
                >
                  <div className="w-4 h-4 bg-white rounded-full" />
                </button>
              </div>
              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div>
                  <p className="font-medium text-stone-800">Marketing</p>
                  <p className="text-xs text-stone-500">Personalized recommendations</p>
                </div>
                <button
                  onClick={() => setConsent({ ...consent, marketing: !consent.marketing })}
                  className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${consent.marketing ? 'bg-teal-600 justify-end' : 'bg-stone-300 justify-start'}`}
                >
                  <div className="w-4 h-4 bg-white rounded-full" />
                </button>
              </div>
            </div>
            <button
              onClick={() => saveConsent(false)}
              className="w-full mt-4 bg-teal-700 hover:bg-teal-800 text-white py-3 rounded-xl font-medium transition-colors"
            >
              Save preferences
            </button>
          </>
        )}
      </div>
    </div>
  );
};
